import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import {
  Col,
  Drawer,
  Form,
  Modal,
  Row,
  Space,
  TableColumnsType,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import { Q_DELETE_EMPLOYEE_TEAM_SALE } from 'apis/GraphQL/Queries/teamSaleQuery';
import { TEAMSALE_VARIABLE } from 'apis/GraphQL/Variables/teamSale';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { XGrayIcon, XIcon } from 'assets/svg';
import { AxiosResponse } from 'axios';
import { Button, ButtonAction } from 'components';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import {
  GetUserNotTS,
  IGetTeamSaleDetailById,
  IGetUserByPlantNotTeamSale,
  IParams,
  Item,
  STATUS_NAME_ST,
} from 'interfaces/TeamSale/TeamSale';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import {
  formatDate,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import ModalMembers from './ModalMembers';

interface Iprops {
  setOpen: any;
  open: boolean;
  children?: ReactNode;
  currentTeam: Item | null;
  getTeamSales({
    afterMutation,
  }: {
    afterMutation?(items: Item[]): void;
  }): void;
  setCurrentTeam: any;
}
const {
  SaveTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleFilled,
  PlusOutlined,
} = icons;
const { confirm } = Modal;

const DrawerSaleTeamPage = ({
  setOpen,
  open,
  currentTeam,
  setCurrentTeam,
  getTeamSales,
}: Iprops) => {
  const { user } = useUser();

  const [form] = Form.useForm();

  const [edit, setEdit] = useState(-1),
    [data, setData] = useState<any>([]),
    [drawerData, setDrawerData] = useState<GetUserNotTS | null>(null),
    [searchValue, setSearchValue] = useState(''),
    [page, setPage] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE }),
    [openModal, setOpenModal] = useState<boolean>(false);

  const searchDebounce = useDebounce(searchValue, 500);

  const columns: TableColumnsType = [
    {
      className: 'shadow-right',
      width: 70,
      // width: 120,
      fixed: 'left',
      align: 'center',
      render: (_: any, record: any, index: number) =>
        edit == index ? (
          <Space size={'middle'} className="text-lg">
            <ButtonAction tooltip="Lưu" onClick={() => handleSave(record)}>
              <SaveTwoTone style={{ fontSize: 20 }} className="" />
            </ButtonAction>
            <ButtonAction tooltip="Hủy" onClick={handleCancel}>
              <CloseCircleTwoTone style={{ fontSize: 20 }} />
            </ButtonAction>
          </Space>
        ) : (
          <Space size={'middle'} className="text-lg ">
            <ButtonAction
              onClick={() => showConfirm(record)}
              tooltip="Xóa"
              disabled={
                currentTeam?.statusName === STATUS_NAME_ST.DEACTIVE ||
                user?.isHO
              }
            >
              {user?.isHO ||
              currentTeam?.statusName === STATUS_NAME_ST.DEACTIVE ? (
                <XGrayIcon />
              ) : (
                <XIcon />
              )}
            </ButtonAction>
          </Space>
        ),
    },
    {
      title: 'STT',
      dataIndex: 'stt',
      className: 'text-end align-top',
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Mã nhân viên',
      dataIndex: ['user', 'id'],
      className: 'text-start align-top',
      width: 120,
    },
    {
      title: 'Tên nhân viên',
      dataIndex: ['user', 'fullName'],
      className: 'text-start align-top',
      width: 250,
    },
    {
      title: 'Ngày vào đội',
      dataIndex: 'createDate',
      className: 'text-start align-top',
      width: 150,
      render: (value: string) => value && formatDate(value),
    },
    {
      title: 'Đội trưởng/Thành viên',
      dataIndex: 'leaderYn',
      className: 'text-start align-top',
      width: 180,
      render: (value) => {
        return value ? 'Đội trưởng' : 'Thành viên';
      },
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'modifyDate',
      className: 'text-start align-top',
      width: 160,
      render: (value) => (value ? formatDate(value) : '-'),
    },
  ];

  const getEmployeesByPlantNotTeamSale = useMutation({
      mutationFn: (params: IGetUserByPlantNotTeamSale) =>
        SaleTeamApi.getUserByPlantId(params),
      onSuccess: (res) => {
        setDrawerData(res.data.getUser);
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    getTeamSaleByIdMutation = useMutation({
      mutationFn: (
        params: IGetTeamSaleDetailById & {
          nextMutate?: UseMutateFunction<
            AxiosResponse<any, any>,
            Error,
            any,
            unknown
          >;
        },
      ) => SaleTeamApi.getAllEmployeeById(params),
      onSuccess: (res, variables) => {
        if (res.data?.getTeamSalesDetail) {
          try {
            // const items = (res.data?.getTeamSalesDetail?.items as any[]).sort(
            //   (a, b) => b.leaderYn - a.leaderYn,
            // );
            const items = res.data?.getTeamSalesDetail?.items as any[];

            setData(items);

            if (
              typeof variables.nextMutate === 'undefined' ||
              typeof user === 'undefined' ||
              !currentTeam
            )
              return;

            variables.nextMutate({
              id: currentTeam.id,
              name: `${currentTeam.id} - ${items
                .map((item: any) => {
                  const nameSplit = item.user.fullName.split(' ');

                  return nameSplit[nameSplit.length - 1];
                })
                .join('_')}`,
              plantId: currentTeam.plantId,
              salesTeamCode: currentTeam.salesTeamCode,
              teamTypeId: currentTeam.teamTypeId,
              statusId: currentTeam.tstatus.id,
              modifyDate: 'now()',
              modifyId: user.id,
              teamCapacity: currentTeam.teamCapacity,
            });
          } catch (error) {
            console.log(error);
          }
        }
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: deleteTeamSaleLoading, mutate: deleteTeamSales } = useMutation(
      {
        mutationFn: (params: IParams) => axiosGraphQL.post('/graphql', params),
        onSuccess: (res) => {
          if (res?.data?.deleteTeamSalesDetail?.success) {
            toastSuccess('Xoá nhân viên khỏi đội thành công!');
            getTeamSales({});

            if (!currentTeam) return;

            getEmployeesByPlantNotTeamSale.mutate({
              plantId: currentTeam.plantId,
              notInTeamSale: currentTeam.id,
            });
            getTeamSaleByIdMutation.mutate({ teamSaleId: currentTeam.id });

            return;
          }
        },
        onError: (error) => {
          toastError(error?.message);
        },
      },
    ),
    updateInfoTeamSaleMutation = useMutation({
      mutationFn: (params: any) => SaleTeamApi.updateSaleTeam(params),
      onSuccess: (res, variables) => {
        if (res.data)
          getTeamSales({
            afterMutation: (items: Item[]) => {
              const currentItem = items.find(
                (item: any) => item.id === variables.id,
              );
              setCurrentTeam(currentItem);
            },
          });
      },
      onError: (error) => {
        getTeamSales({});
        console.log(error);
      },
    });

  const showConfirm = (record: any) => {
    const now = new Date();
    const formattedDate = dayjs(now).format('YYYY-MM-DD HH:mm:ss');

    const params: IParams = {
      query: Q_DELETE_EMPLOYEE_TEAM_SALE,
      variables: TEAMSALE_VARIABLE.deleteEmployeeTeamSale(
        record.id,
        formattedDate,
      ),
    };

    confirm({
      title: `Bạn có muốn xoá ${record.user.fullName} ra khỏi đội ?`,
      icon: <ExclamationCircleFilled />,
      onOk() {
        deleteTeamSales(params);
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const replaceData = (index: number, value: any) => {
    const newData = [...data];
    newData.splice(index, 1, value);
    setData(newData);
  };

  const handleSave = (record: any) => {
    replaceData(edit, { ...record, ...form.getFieldsValue() });
    form.resetFields();
    setEdit(-1);
  };

  const handleCancel = () => {
    form.resetFields();
    setEdit(-1);
  };

  const onClose = () => {
    setOpen(false);
    setCurrentTeam(null);
    setSearchValue('');
  };

  useEffect(() => {
    if (!currentTeam || !open) return;

    open &&
      getTeamSaleByIdMutation.mutate({
        teamSaleId: currentTeam?.id,
        useYn: currentTeam?.statusName === STATUS_NAME_ST.ACTIVE,
        ...page,
      });
  }, [open, currentTeam, page]);

  useEffect(() => {
    if (currentTeam === null) return;

    getTeamSaleByIdMutation.mutate({
      teamSaleId: currentTeam.id,
      user: searchDebounce.trim(),
    });
  }, [searchDebounce]);

  // const handleSearch = () => {
  //   const value = searchValue.trim();

  //   if (value === '' || currentTeam === null) return;
  //   getTeamSaleByIdMutation.mutate({ teamSaleId: currentTeam.id, user: value });
  // };

  const handleClickCreate = useCallback(() => {
    setOpenModal((prev) => !prev);
    setSearchValue('');
  }, []);

  return (
    <Drawer
      width={885}
      title={
        <Typography.Text className="text-[#003056] font-bold text-2xl">
          Nhân viên trong {currentTeam?.name}
        </Typography.Text>
      }
      onClose={onClose}
      open={open}
      closable={false}
      destroyOnClose
      extra={<ButtonAction title={<XGrayIcon />} onClick={onClose} />}
    >
      <Row
        gutter={[8, 12]}
        justify={'end'}
        align={'middle'}
        className="mb-[22px]"
      >
        {user?.isHO ? (
          <></>
        ) : (
          <Col>
            <Button
              handleOnclick={handleClickCreate}
              title={<PlusOutlined />}
              disabled={
                user?.isHO ||
                currentTeam?.statusName === STATUS_NAME_ST.DEACTIVE
              }
            />
          </Col>
        )}
      </Row>
      <ModalMembers
        teamSale={currentTeam ?? undefined}
        open={openModal}
        data={drawerData}
        setData={setDrawerData}
        setOpen={setOpenModal}
        getTSById={getTeamSaleByIdMutation}
        refetch={getTeamSales}
      />
      <TableComponent
        rowKey={(record) => record.id}
        dataSource={data}
        columns={columns}
        loading={
          getTeamSaleByIdMutation.isPending ||
          deleteTeamSaleLoading ||
          updateInfoTeamSaleMutation.isPending
        }
        pagination={{
          current: page.page,
          pageSize: page.pageSize,
          onChange(page, pageSize) {
            setPage({ page, pageSize });
          },
        }}
        scroll={{ x: 875 }}
      />
    </Drawer>
  );
};
export default DrawerSaleTeamPage;
